<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAccountResource } from 'dashboard/composables/useAccountResource';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import GgContactInfo from './contact/GgContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import GgSupportTickets from './GgSupportTickets.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  uiSettings,
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
  isOnExpandedLayout,
} = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const accountCustomerSearchEnabled = useAccountResource('gg_customer_search');
const ggCustomerSearchEnabled = computed(
  () =>
    accountCustomerSearchEnabled.value &&
    window.chatwootConfig?.ggCustomerSearchEnabled &&
    String(currentAccountId.value) === window.chatwootConfig.ggFollowupAccountId
);

const dragging = ref(false);
const attachmentsExpanded = ref(false);
const contactNotesExpanded = ref(false);
const contactDetailsExpanded = ref(false);
const conversationSidebarItems = ref([]);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
watch(conversationId, () => {
  attachmentsExpanded.value = false;
  contactNotesExpanded.value = false;
  contactDetailsExpanded.value = false;
});
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const appliedContactFilter = useMapGetter('getAppliedContactFilter');

const isListScopedToContact = computed(
  () =>
    !isOnExpandedLayout.value &&
    appliedContactFilter.value?.id === contactId.value
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="w-full min-w-0">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @close="closeContactPanel"
    />
    <ContactInfo
      :contact="contact"
      :channel-type="channelType"
      :show-details="false"
    />
    <div class="px-2 pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
              <div class="mt-4 border-t border-n-weak pt-3">
                <h6 class="mb-2 text-sm font-medium text-n-slate-12">
                  {{ $t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE') }}
                </h6>
                <ConversationParticipant
                  :conversation-id="conversationId"
                  :inbox-id="inboxId"
                />
              </div>
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_information'">
            <AccordionItem
              :title="$t('CONTACT_INFORMATION.TITLE')"
              :is-open="uiSettings.is_contact_information_open ?? true"
              compact
              @toggle="
                updateUISettings({
                  is_contact_information_open: !(
                    uiSettings.is_contact_information_open ?? true
                  ),
                })
              "
            >
              <div class="divide-y divide-n-weak">
                <section
                  :aria-label="$t('CONTACT_INFORMATION.SUPPLEMENTARY_TITLE')"
                >
                  <h6 class="m-0">
                    <button
                      type="button"
                      class="m-0 flex w-full items-center justify-between gap-3 rounded-lg bg-n-slate-2 px-4 py-3 text-start text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-n-brand"
                      :aria-expanded="contactDetailsExpanded"
                      :aria-controls="`contact-details-${conversationId}`"
                      @click="contactDetailsExpanded = !contactDetailsExpanded"
                    >
                      {{ $t('CONTACT_INFORMATION.SUPPLEMENTARY_TITLE') }}
                      <div class="flex flex-row" aria-hidden="true">
                        <div
                          class="flex justify-end w-3 text-n-blue-11 cursor-pointer"
                        >
                          <fluent-icon
                            :icon="contactDetailsExpanded ? 'subtract' : 'add'"
                            size="24"
                            type="solid"
                          />
                        </div>
                      </div>
                    </button>
                  </h6>
                  <div
                    :id="`contact-details-${conversationId}`"
                    :hidden="!contactDetailsExpanded"
                    class="divide-y divide-n-weak"
                  >
                    <section
                      :aria-label="$t('CONTACT_INFORMATION.CHATWOOT_TITLE')"
                    >
                      <h6
                        class="m-0 px-4 pt-3 text-sm font-medium text-n-slate-12"
                      >
                        {{ $t('CONTACT_INFORMATION.CHATWOOT_TITLE') }}
                      </h6>
                      <ContactInfo
                        :contact="contact"
                        :show-avatar="false"
                        :show-actions="false"
                      />
                    </section>
                    <section
                      :aria-label="$t('CONTACT_INFORMATION.ATTRIBUTES_TITLE')"
                    >
                      <h6
                        class="m-0 px-4 pt-3 text-sm font-medium text-n-slate-12"
                      >
                        {{ $t('CONTACT_INFORMATION.ATTRIBUTES_TITLE') }}
                      </h6>
                      <CustomAttributes
                        attribute-type="contact_attribute"
                        attribute-from="conversation_contact_panel"
                        :contact-id="contact.id"
                        :empty-state-message="
                          $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                        "
                      />
                    </section>
                  </div>
                </section>
                <section
                  v-if="ggCustomerSearchEnabled"
                  :aria-label="$t('GG_CONTACT.GG_TITLE')"
                  class="px-4 py-3"
                >
                  <h6 class="m-0 mb-3 text-sm font-medium text-n-slate-12">
                    {{ $t('GG_CONTACT.GG_TITLE') }}
                  </h6>
                  <GgContactInfo
                    v-if="contact.id"
                    :key="`${conversationId}-${contact.id}`"
                    :contact="contact"
                    :conversation-id="Number(conversationId)"
                  />
                </section>
                <section
                  :aria-label="
                    $t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')
                  "
                >
                  <h6 class="m-0">
                    <button
                      type="button"
                      class="m-0 flex w-full items-center justify-between gap-3 rounded-lg bg-n-slate-2 px-4 py-3 text-start text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-n-brand"
                      :aria-expanded="contactNotesExpanded"
                      :aria-controls="`contact-notes-${contactId}`"
                      @click="contactNotesExpanded = !contactNotesExpanded"
                    >
                      {{ $t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES') }}
                      <div class="flex flex-row" aria-hidden="true">
                        <div
                          class="flex justify-end w-3 text-n-blue-11 cursor-pointer"
                        >
                          <fluent-icon
                            :icon="contactNotesExpanded ? 'subtract' : 'add'"
                            size="24"
                            type="solid"
                          />
                        </div>
                      </div>
                    </button>
                  </h6>
                  <div
                    v-if="contactNotesExpanded"
                    :id="`contact-notes-${contactId}`"
                  >
                    <ContactNotes
                      v-if="contactId"
                      :key="contactId"
                      :contact-id="contactId"
                    />
                  </div>
                </section>
              </div>
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
              <GgSupportTickets :conversation-id="Number(conversationId)" />
              <section
                :aria-label="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
                class="border-t border-n-weak"
              >
                <h6 class="m-0">
                  <button
                    type="button"
                    class="m-0 flex w-full items-center justify-between gap-3 rounded-lg bg-n-slate-2 px-4 py-3 text-start text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-n-brand"
                    :aria-expanded="attachmentsExpanded"
                    :aria-controls="`conversation-attachments-${conversationId}`"
                    @click="attachmentsExpanded = !attachmentsExpanded"
                  >
                    {{ $t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES') }}
                    <div class="flex flex-row" aria-hidden="true">
                      <div
                        class="flex justify-end w-3 text-n-blue-11 cursor-pointer"
                      >
                        <fluent-icon
                          :icon="attachmentsExpanded ? 'subtract' : 'add'"
                          size="24"
                          type="solid"
                        />
                      </div>
                    </div>
                  </button>
                </h6>
                <div
                  v-if="attachmentsExpanded"
                  :id="`conversation-attachments-${conversationId}`"
                >
                  <SharedFiles />
                </div>
              </section>
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'previous_conversation' && !isListScopedToContact
            "
          >
            <AccordionItem
              v-if="contact.id"
              :title="$t('CONTACT_INFORMATION.HISTORY_TITLE')"
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
